import React from 'react';

/**
 * ErrorBoundary — catches render-time crashes inside its children and
 * shows a friendly "something went wrong" panel instead of letting React
 * unmount the whole tree (which is what shows the dreaded blank-white-page).
 *
 * Usage:
 *   <PageErrorBoundary>
 *     <MyPage />
 *   </PageErrorBoundary>
 */
export default class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '', errorStack: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message || String(error),
      errorStack: error?.stack || '',
    };
  }

  componentDidCatch(error, info) {
    // Surface for production debugging — visible in browser console & monitoring.
    console.error('[PageErrorBoundary]', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 max-w-3xl mx-auto" data-testid="page-error-boundary">
          <div className="bg-red-50 border border-red-300 rounded-lg p-6">
            <h2 className="text-lg font-bold text-red-800 mb-2">⚠️ Something went wrong on this page</h2>
            <p className="text-sm text-red-700 mb-3">
              The page hit an error while loading. Try the steps below — they fix this 99% of the time:
            </p>
            <ol className="text-sm text-slate-700 space-y-1 list-decimal pl-5 mb-4">
              <li><b>Hard refresh</b> — Ctrl+Shift+R (Windows) / Cmd+Shift+R (Mac)</li>
              <li>If that doesn't help, open <b>DevTools (F12) → Application → Storage → Clear site data</b>, then login again</li>
              <li>Try opening this page in <b>Incognito / Private window</b></li>
            </ol>
            <details className="mt-4 text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-800">Show technical details</summary>
              <div className="mt-2 bg-white border border-slate-200 rounded p-2 font-mono text-[10px] overflow-x-auto">
                <div className="font-semibold text-red-700">{this.state.errorMessage}</div>
                {this.state.errorStack && (
                  <pre className="mt-2 text-slate-500 whitespace-pre-wrap">{this.state.errorStack.split('\n').slice(0, 8).join('\n')}</pre>
                )}
              </div>
            </details>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
